<template>
  <div class="p-fluid">
    <div class="p-formgrid p-grid">
      <div :class="['p-field', col]">
        <label for="pseQfCust">Customer *</label>
        <AutoComplete id="pseQfCust" v-model="form.customer" :suggestions="form.customerSuggestions" field="custName" placeholder="Type 3+ letters of the customer"
          :minLength="3" :delay="350" @complete="form.findCustomers($event.query)" @item-select="form.onCustomer($event.value)"
          :class="{ 'p-invalid': form.shown('customerId') }">
          <template #item="slotProps">
            {{ slotProps.item.custName }}
            <span class="pse-muted">{{ [slotProps.item.accountCode, slotProps.item.postCode, slotProps.item.town].filter(Boolean).join(' · ') }}</span>
          </template>
        </AutoComplete>
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfContact">Contact{{ form.req('contactId') ? ' *' : '' }}</label>
        <Dropdown id="pseQfContact" v-model="form.contact" :options="form.contacts" optionLabel="contactName" dataKey="contactId" :showClear="true"
          placeholder="Select Contact" :disabled="!form.customerId" @change="form.onContact" :class="{ 'p-invalid': form.shown('contactId') }" />
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfBranch">Branch{{ form.req('branchId') ? ' *' : '' }}</label>
        <Dropdown id="pseQfBranch" v-model="form.branchId" :options="form.branches" optionLabel="branchName" optionValue="branchId"
          placeholder="Select Branch" :disabled="!form.customerId || form.onlyUserBranch" :class="{ 'p-invalid': form.shown('branchId') }" />
        <small v-if="form.salesExecName" class="pse-muted">Sales Exec: {{ form.salesExecName }}</small>
        <small v-if="form.noOwnBranch" class="p-invalid">This customer is not set up for your branch, so you cannot add it.</small>
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfPt">Product Type{{ form.req('productTypeId') ? ' *' : '' }}</label>
        <Dropdown id="pseQfPt" v-model="form.productTypeId" :options="form.productTypes" optionLabel="productTypeName" optionValue="id" :filter="true"
          placeholder="Select Product Type" :class="{ 'p-invalid': form.shown('productTypeId') }" />
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfPhone">Phone No{{ form.req('phoneNo') ? ' *' : '' }}</label>
        <InputText id="pseQfPhone" v-model.trim="form.phoneNo" :class="{ 'p-invalid': form.shown('phoneNo') }" />
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfEmail">Email{{ form.req('email') ? ' *' : '' }}</label>
        <InputText id="pseQfEmail" v-model.trim="form.email" :class="{ 'p-invalid': form.shown('email') }" />
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfType">Enquiry Type{{ form.req('projectTypeId') ? ' *' : '' }}</label>
        <Dropdown id="pseQfType" v-model="form.projectTypeId" :options="form.projectTypes" optionLabel="projTypeName" optionValue="id" :showClear="true"
          placeholder="Select Enquiry Type" :class="{ 'p-invalid': form.shown('projectTypeId') }" />
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfChance">Chance of Order{{ form.req('chance') ? ' *' : '' }}</label>
        <Dropdown id="pseQfChance" v-model="form.chance" :options="form.chances" optionLabel="label" optionValue="value" :showClear="true"
          placeholder="A / B / C" :class="{ 'p-invalid': form.shown('chance') }" />
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfMargin">Branch Margin (%){{ form.req('branchMargin') ? ' *' : '' }}</label>
        <InputNumber id="pseQfMargin" v-model="form.branchMargin" mode="decimal" :minFractionDigits="0" :maxFractionDigits="2" suffix=" %"
          :class="{ 'p-invalid': form.shown('branchMargin') }" />
      </div>
      <div :class="['p-field', col]">
        <label for="pseQfChaser">Who will be chasing{{ form.req('chaserUserId') ? ' *' : '' }}</label>
        <Dropdown id="pseQfChaser" v-model="form.chaserUserId" :options="form.users" optionLabel="fullName" optionValue="id" :filter="true" :showClear="true"
          placeholder="Select User" :class="{ 'p-invalid': form.shown('chaserUserId') }" />
      </div>
    </div>

    <template v-if="inlineCheck">
    <div v-if="form.checking" class="pse-note info"><i class="pi pi-spin pi-spinner"></i><span>Checking this customer against the enquiry...</span></div>
    <div v-else-if="form.display.severity" :class="['pse-note', form.display.severity, 'pse-check']">
      <i :class="form.display.severity === 'error' ? 'pi pi-ban' : 'pi pi-exclamation-triangle'"></i>
      <span>
        {{ form.display.text }}
        <span v-if="form.display.needsConfirm"><br />
          <Button label="Add a new quotation anyway" class="p-button-sm p-button-warning pse-add-anyway" style="margin-top: 6px; width: auto;" @click="form.confirmed = true" />
        </span>
        <span v-else-if="form.check && form.check.result === 'WARN' && form.confirmed"><br /><strong>Confirmed: a new quotation will be added, with its own quote number.</strong></span>
      </span>
    </div>
    <DataTable v-if="!form.checking && form.existing.length" :value="form.existing" dataKey="oppId"
      class="p-datatable-striped p-datatable-sm p-datatable-gridlines p-mb-3 pse-existing-quotes">
      <Column field="sQuoteNo" header="Existing Quote No">
        <template #body="s">{{ s.data.sQuoteNo || 'No quotation yet' }}</template>
      </Column>
      <Column field="productType" header="Product Type" />
      <Column field="branch" header="Branch" />
      <Column headerStyle="width: 12em;">
        <template #body="s">
          <Button :label="s.data.canCreateVersion ? 'Create New Version' : 'Open enquiry'" :icon="s.data.canCreateVersion ? 'pi pi-copy' : 'pi pi-external-link'"
            class="p-button-sm p-button-outlined pse-new-version" @click="form.openNewVersion(s.data)" />
        </template>
      </Column>
    </DataTable>
    </template>
    <div v-else-if="form.check && form.check.result === 'WARN' && form.confirmed" class="pse-muted pse-confirmed">
      <i class="pi pi-check"></i> {{ form.customer && form.customer.custName }} already has a quotation on this enquiry: a new quotation will be added, with its own quote number.
    </div>
  </div>
</template>

<script>
// Designs C and D: the customer quote fields, the customer check and the existing quotes, bound to the component that
// holds the bidForm mixin (form), so the side panel (C) and the Customer quote tab (D) show the same thing.
export default {
  name: 'QuoteFields',
  props: {
    form: { type: Object, required: true },
    // one column in C's side panel, two in D's dialog
    col: { type: String, default: 'p-col-6' },
    // false in design D: the duplicate question is asked in a modal over the form instead (DuplicateQuestion)
    inlineCheck: { type: Boolean, default: true }
  }
}
</script>

<style scoped>
@import './sharedEnquiry.css';
</style>
